import {
  type AttributeInput,
  type CartFragmentFragment,
} from '@data/shopify/storefront/types'
import { type Cart, type CartFormValues, type LineItem } from '@lib/cart/types'
import { type Locale } from '@lib/language'
import { getSanityClient } from '@lib/sanity/client'
import { getProductVariants } from '@lib/sanity/product-variant'
import { type CartFragmentFragmentLine } from './graphql/cart'
import { parseShopifyGlobalId } from './helpers'

export const preorderShopifyAttribute = '_preorder'
export const taxExemptShopifyAttribute = '_tax_exempt'
export const taxVatIdShopifyAttribute = '_vat_id'

/**
 * Gets tax-related cart attributes.
 */
export const getTaxCartAttributes = (
  cartFormValues: CartFormValues,
  taxExempt: boolean,
) => {
  const customAttributes: AttributeInput[] = []

  if (cartFormValues.vatId) {
    // Set VAT ID checkout attribute
    customAttributes.push({
      key: taxVatIdShopifyAttribute,
      value: cartFormValues.vatId,
    })

    // Set tax exemption checkout attribute
    customAttributes.push({
      key: taxExemptShopifyAttribute,
      value: taxExempt ? 'yes' : 'no',
    })
  }

  return customAttributes
}

/**
 * Parses Shopify line items.
 */
const parseShopifyCartLineItems = async (
  shopifyLineItems: CartFragmentFragmentLine[],
  locale: Locale,
) => {
  // Read product variants in cart
  const cartVariantIds = shopifyLineItems
    .map((shopifyLineItem) => {
      if (!shopifyLineItem.merchandise.id) {
        return
      }

      return parseShopifyGlobalId(`${shopifyLineItem.merchandise.id}`)
    })
    .filter(Boolean) as number[]
  const sanityClient = getSanityClient()
  const productVariants = await getProductVariants(
    sanityClient,
    locale,
    cartVariantIds,
  )

  const lineItems = shopifyLineItems.map((shopifyLineItem) => {
    const variantId = shopifyLineItem.merchandise.id
      ? parseShopifyGlobalId(`${shopifyLineItem.merchandise.id}`)
      : null

    const variant = variantId
      ? productVariants.find(
          (productVariant) => productVariant.variantID === variantId,
        )
      : null

    if (!variant) {
      return
    }

    const customAttributes = shopifyLineItem.attributes.map((attribute) => {
      const attributeInput: AttributeInput = {
        key: attribute.key,
        value: attribute.value ?? '',
      }

      return attributeInput
    })

    const variantLineItem: LineItem = {
      lineId: shopifyLineItem.id,
      quantity: shopifyLineItem.quantity,
      customAttributes,
      ...variant,
    }

    return variantLineItem
  })

  return lineItems.filter(Boolean) as LineItem[]
}

/**
 * Parses Shopify cart.
 */
export const parseShopifyCart = async (
  locale: Locale,
  shopifyCart?: CartFragmentFragment,
) => {
  if (!shopifyCart) {
    return
  }

  const lineItems = await parseShopifyCartLineItems(
    shopifyCart.lines.edges.map((edge) => edge.node),
    locale,
  )

  const subTotal = shopifyCart.estimatedCost.subtotalAmount
    ? Number(shopifyCart.estimatedCost.subtotalAmount.amount) * 100
    : 0

  const automaticDiscount = shopifyCart.lines.edges.reduce(
    (result, edge) => {
      const discountAllocation = edge.node.discountAllocations[0]

      if (
        discountAllocation &&
        'title' in discountAllocation &&
        'discountedAmount' in discountAllocation
      ) {
        return {
          title: discountAllocation.title,
          amount:
            result.amount +
            parseFloat(discountAllocation.discountedAmount.amount) * 100,
        }
      }

      return result
    },
    {
      title: '',
      amount: 0,
    },
  )

  const total = subTotal - automaticDiscount.amount

  const cart: Cart = {
    id: shopifyCart.id,
    lineItems,
    automaticDiscount,
    discountCodes: shopifyCart.discountCodes.map(({ code }) => code),
    subTotal,
    total,
    webUrl: `${shopifyCart.checkoutUrl}`,
  }

  return cart
}
